import React from 'react';
import { Link } from 'react-router-dom';
import { Box, Typography, useTheme } from '@mui/material';
import AdBanner from './AdBanner';
import References from './References';

const About: React.FC = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  return (
    <Box sx={{ maxWidth: '800px', mx: 'auto', px: 3, py: 4 }}>
      {/* Hero */}
      <Box sx={{ mb: 6 }}>
        <Typography
          variant="h2"
          sx={{
            fontSize: { xs: '36px', sm: '48px' },
            fontWeight: 700,
            lineHeight: 1.1,
            mb: 2,
            color: theme.palette.text.primary,
            letterSpacing: '-0.02em',
          }}
        >
          About emotify
        </Typography>
        <Typography
          sx={{
            fontSize: '18px',
            color: theme.palette.text.secondary,
            fontWeight: 500,
          }}
        >
          Visualizing the emotional landscape of your music
        </Typography>
      </Box>

      {/* Content Sections */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
        {/* What is emotify */}
        <Box>
          <Typography
            variant="h5"
            sx={{
              fontSize: '24px',
              fontWeight: 700,
              mb: 2,
              color: theme.palette.text.primary,
            }}
          >
            What is emotify?
          </Typography>
          <Typography
            sx={{
              fontSize: '16px',
              lineHeight: 1.7,
              color: theme.palette.text.secondary,
              mb: 2,
            }}
          >
            emotify is a music visualization tool that maps your listening habits onto an emotional landscape. 
            By analyzing the acoustic properties of your favorite tracks, we create a visual heatmap that reveals 
            the emotional patterns in your music taste. Whether your playlist leans melancholic, energetic, or 
            spans the full spectrum of human emotion, emotify helps you understand and share the feelings behind 
            your soundtrack.
          </Typography>
          <Typography
            sx={{
              fontSize: '16px',
              lineHeight: 1.7,
              color: theme.palette.text.secondary,
            }}
          >
            emotify works only with public playlists and needs no account, login, or personal information —
            you never connect your Spotify, YouTube Music or Apple Music profile. To build a heatmap, the public playlist
            you paste is processed by our service to look up each track's audio features. We collect anonymous,
            aggregate usage stats (like how many playlists have been analyzed) but never tie them to you. For the
            full details, see our <Link to="/privacy" style={{ color: theme.palette.primary.main, fontWeight: 600 }}>Privacy Policy</Link>.
          </Typography>
        </Box>

        {/* The Science of Musical Emotion */}
        <Box>
          <Typography
            variant="h5"
            sx={{
              fontSize: '24px',
              fontWeight: 700,
              mb: 2,
              color: theme.palette.text.primary,
            }}
          >
            The Science of Musical Emotion
          </Typography>
          <Typography
            sx={{
              fontSize: '16px',
              lineHeight: 1.7,
              color: theme.palette.text.secondary,
              mb: 3,
            }}
          >
            Music emotion research has established that feelings evoked by music can be mapped using two fundamental 
            dimensions: valence (pleasantness) and arousal (energy). This model, known as the circumplex model of affect, 
            was pioneered by psychologist James Russell and has been extensively validated in music psychology research 
            (Russell, 1980; Thayer, 1989).
          </Typography>

          {/* Emotional Axis Visualization */}
          <Box
            sx={{
              my: 4,
              p: 5,
              borderRadius: '20px',
              background: theme.palette.background.paper,
              border: `1px solid ${theme.palette.divider}`,
              boxShadow: isDark ? '0 4px 20px rgba(0,0,0,0.3)' : '0 4px 20px rgba(0,0,0,0.06)',
              display: 'flex',
              justifyContent: 'center',
            }}
          >
            <svg width="600" height="500" viewBox="0 0 600 500" style={{ maxWidth: '100%', height: 'auto', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif' }}>
              {/* Background */}
              <rect width="600" height="500" fill={isDark ? '#1E1E1E' : 'white'} />
              
              {/* Axes */}
              <line x1="300" y1="60" x2="300" y2="420" stroke={isDark ? '#555' : '#ccc'} strokeWidth="2" strokeDasharray="5,5" />
              <line x1="130" y1="240" x2="470" y2="240" stroke={isDark ? '#555' : '#ccc'} strokeWidth="2" strokeDasharray="5,5" />
              
              {/* Axis labels */}
              <text x="300" y="40" textAnchor="middle" fontSize="15" fontWeight="600" fill={isDark ? '#E0E0E0' : '#000'}>High Energy</text>
              <text x="300" y="460" textAnchor="middle" fontSize="15" fontWeight="600" fill={isDark ? '#E0E0E0' : '#000'}>Low Energy</text>
              <text x="80" y="245" textAnchor="middle" fontSize="15" fontWeight="600" fill={isDark ? '#E0E0E0' : '#000'}>Low Valence</text>
              <text x="520" y="245" textAnchor="middle" fontSize="15" fontWeight="600" fill={isDark ? '#E0E0E0' : '#000'}>High Valence</text>
              
              {/* Quadrant labels */}
              <text x="200" y="130" textAnchor="middle" fontSize="16" fontWeight="600" fill={isDark ? '#ccc' : '#333'}>Turbulent</text>
              <text x="200" y="150" textAnchor="middle" fontSize="13" fill={isDark ? '#999' : '#666'}>(Angry, Tense)</text>
              
              <text x="400" y="130" textAnchor="middle" fontSize="16" fontWeight="600" fill={isDark ? '#ccc' : '#333'}>Energetic</text>
              <text x="400" y="150" textAnchor="middle" fontSize="13" fill={isDark ? '#999' : '#666'}>(Happy, Excited)</text>
              
              <text x="200" y="350" textAnchor="middle" fontSize="16" fontWeight="600" fill={isDark ? '#ccc' : '#333'}>Melancholic</text>
              <text x="200" y="370" textAnchor="middle" fontSize="13" fill={isDark ? '#999' : '#666'}>(Sad, Depressed)</text>
              
              <text x="400" y="350" textAnchor="middle" fontSize="16" fontWeight="600" fill={isDark ? '#ccc' : '#333'}>Peaceful</text>
              <text x="400" y="370" textAnchor="middle" fontSize="13" fill={isDark ? '#999' : '#666'}>(Calm, Relaxed)</text>
              
              {/* Center point */}
              <circle cx="300" cy="240" r="5" fill={isDark ? '#E0E0E0' : '#000'} />
              
              {/* Border */}
              <rect x="130" y="60" width="340" height="360" fill="none" stroke={isDark ? '#444' : '#ddd'} strokeWidth="2" rx="12" />
            </svg>
          </Box>

          <Typography
            sx={{
              fontSize: '16px',
              lineHeight: 1.7,
              color: theme.palette.text.secondary,
              mb: 2,
            }}
          >
            Research by Eerola and Vuoskoski (2011) demonstrated that these dimensions can be reliably extracted 
            from acoustic features of music. Studies have shown that tempo, loudness, and spectral characteristics 
            correlate strongly with perceived energy, while harmonic complexity and mode (major vs. minor) influence 
            valence perception (Gabrielsson & Lindström, 2001).
          </Typography>
        </Box>

        {/* Why It Matters */}
        <Box>
          <Typography
            variant="h5"
            sx={{
              fontSize: '24px',
              fontWeight: 700,
              mb: 2,
              color: theme.palette.text.primary,
            }}
          >
            Why Understanding Your Music Mood Matters
          </Typography>
          <Typography
            sx={{
              fontSize: '16px',
              lineHeight: 1.7,
              color: theme.palette.text.secondary,
              mb: 2,
            }}
          >
            Music has a profound impact on our emotional well-being. Understanding the emotional patterns in your 
            listening habits can be both therapeutic and enlightening. Research in music therapy shows that conscious 
            engagement with music's emotional content helps with mood regulation, stress reduction, and self-awareness 
            (Thoma et al., 2013).
          </Typography>
          <Typography
            sx={{
              fontSize: '16px',
              lineHeight: 1.7,
              color: theme.palette.text.secondary,
            }}
          >
            Beyond the therapeutic benefits, exploring your music's emotional heatmap is simply fun. It's a unique 
            way to see yourself reflected in your listening habits, share your musical personality with friends, and 
            discover new dimensions of the songs you love. You might be surprised to find that your "chill" playlist 
            is actually quite energetic, or that your workout music has more emotional complexity than you realized.
          </Typography>
        </Box>

        {/* References */}
        <References />

        {/* Ad Banner */}
        <AdBanner />
      </Box>

    </Box>
  );
};

export default About;
